import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Share2, ArrowRight, Flame, Trophy } from "lucide-react";

const BADGE_LABEL = {
    avant_apres: "Avant / Après",
    objectif_atteint: "Objectif Atteint",
};

export default function ProgressFeed() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        (async () => {
            const { data } = await api.get("/feed");
            setItems(data);
        })();
    }, []);

    const share = async (e, itemId) => {
        e.preventDefault();
        e.stopPropagation();
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/story/${itemId}`;
        try {
            if (navigator.share) {
                await navigator.share({ url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Lien de partage copié");
            }
        } catch {
            // user cancelled
        }
    };

    return (
        <div className="bg-black">
            {/* Page header */}
            <header className="border-b border-white/10 bg-[#0A0A0A]">
                <div className="mx-auto flex h-[92px] max-w-[1400px] items-end justify-between px-6 pb-4">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                            Progrès vérifiés
                        </div>
                        <h1 className="mt-1 font-display text-3xl uppercase leading-none tracking-tight text-white sm:text-4xl">
                            Fil de Progression
                        </h1>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[#FF2E2E]">
                            Vrais résultats d&apos;élèves
                        </p>
                    </div>
                    <span className="hidden pb-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500 md:inline">
                        {items.length} témoignages · 100 % consentis
                    </span>
                </div>
            </header>

            {/* Feed */}
            <div className="feed-container">
                {items.map((p, idx) => {
                    const badgeKey = p.badge || "objectif_atteint";
                    const BadgeIcon = badgeKey === "avant_apres" ? Flame : Trophy;
                    const ageBit = p.student_age ? `, ${p.student_age} ans` : "";
                    return (
                        <section
                            key={p.item_id}
                            data-testid={`feed-card-${p.item_id}`}
                            className="feed-item relative flex w-full items-stretch justify-center"
                        >
                            <div className="relative mx-auto flex h-full w-full max-w-[520px] overflow-hidden border-x border-white/10">
                                <img
                                    src={p.media_url}
                                    alt={p.headline}
                                    className="h-full w-full object-cover"
                                />
                                {/* Overlay gradient */}
                                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                                {/* Top: index + category */}
                                <div className="absolute left-0 right-0 top-0 flex items-center justify-between p-4">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">
                                        #{String(idx + 1).padStart(2, "0")}
                                    </span>
                                    <span
                                        className={`font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 ${
                                            p.coach_category === "physical"
                                                ? "bg-[#FF2E2E] text-white"
                                                : "border border-white/40 bg-black/60 text-white"
                                        }`}
                                    >
                                        {p.coach_category === "physical" ? "Corps" : "Esprit"}
                                    </span>
                                </div>

                                {/* Primary badge — Avant/Après or Objectif Atteint */}
                                <div
                                    data-testid={`feed-badge-${badgeKey}`}
                                    className="absolute left-4 top-14 inline-flex items-center gap-2 border-l-2 border-[#FF2E2E] bg-black/60 px-3 py-1.5 font-display text-sm uppercase tracking-tight text-white backdrop-blur-sm"
                                >
                                    <BadgeIcon className="h-3.5 w-3.5 text-[#FF2E2E]" />
                                    {BADGE_LABEL[badgeKey]}
                                </div>

                                {/* Bottom content */}
                                <div className="absolute inset-x-0 bottom-0 space-y-4 p-6">
                                    <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-200">
                                        {p.student_name}{ageBit}
                                        <span className="text-[#FF2E2E]"> — </span>
                                        <Clock className="inline h-3 w-3" /> {p.duration_label}
                                    </div>

                                    <h2 className="font-display text-3xl uppercase leading-tight text-white sm:text-4xl">
                                        {p.headline}
                                    </h2>

                                    <div className="flex flex-wrap items-center gap-2 pt-2">
                                        <Link
                                            to={`/coach/${p.coach_id}`}
                                            data-testid={`feed-view-coach-${p.item_id}`}
                                            className="inline-flex items-center gap-2 border border-[#FF2E2E] bg-[#FF2E2E] px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest text-white transition-colors hover:bg-[#D61F1F]"
                                        >
                                            <img
                                                src={p.coach_photo}
                                                alt=""
                                                className="h-5 w-5 object-cover"
                                            />
                                            Voir le profil du coach
                                            <ArrowRight className="h-3.5 w-3.5" />
                                        </Link>
                                        <Link
                                            to={`/story/${p.item_id}`}
                                            data-testid={`story-open-${p.item_id}`}
                                            className="inline-flex items-center gap-2 border border-white/20 bg-black/50 px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest text-white backdrop-blur-sm transition-colors hover:border-[#FF2E2E]"
                                        >
                                            Ouvrir l&apos;histoire
                                        </Link>
                                        <button
                                            data-testid={`story-share-${p.item_id}`}
                                            onClick={(e) => share(e, p.item_id)}
                                            aria-label="Partager"
                                            className="inline-flex items-center justify-center border border-white/20 bg-black/50 p-2.5 text-white backdrop-blur-sm transition-colors hover:border-[#FF2E2E] hover:text-[#FF2E2E]"
                                        >
                                            <Share2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </section>
                    );
                })}
                {items.length === 0 && (
                    <div className="flex h-full items-center justify-center py-32 text-neutral-500">
                        Aucune histoire pour l&apos;instant.
                    </div>
                )}
            </div>
        </div>
    );
}
